// components/rapport/common/ExportClubReportMenu.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import DownloadClubPdfButton from "../reportspages/club/DownloadClubPdfButton";
import { usePdfExport } from "../pdf/PdfExportContext"; // << NEW

// --- Utils CSV
const csvEscape = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const toCSV = (rows) => rows.map(r => r.map(csvEscape).join(",")).join("\n");

const fmtVal = (v, { percent = false, unit = "", decimals = 2 } = {}) => {
  if (v == null || !Number.isFinite(v)) return "";
  return percent ? (v * 100).toFixed(1) : Number(v).toFixed(decimals);
};
const fmtDelta = (d, { percent = false, unit = "", decimals = 2 } = {}) => {
  if (d == null || !Number.isFinite(d)) return "";
  const sign = d > 0 ? "+" : d < 0 ? "-" : "=";
  const val = Math.abs(percent ? d * 100 : d).toFixed(percent ? 1 : decimals);
  return `${sign}${val}${percent ? "%" : unit}`;
};
const trainabilityScore = (row) => {
  const score = Number(row?.score ?? row?.rii);
  return Number.isFinite(score) ? score : null;
};
const fmtTrainabilityScore = (row) => {
  const score = trainabilityScore(row);
  return score == null ? "" : score.toFixed(1);
};

// Labels (align with your ClubReport/Trends)
const METRIC_LABELS = {
  "internal.mechanical_fatigue": "Mechanical Fatigue (%)",
  "internal.intensity_index": "Intensity (PT)",
  "internal.mechanical_load_index": "Mechanical Load (PT)",
  "internal.power": "Max horizontal mechanical power (W/kg)",
  "external.accelerations": "Accelerations max (m/s²)",
  "external.decelerations": "Decelerations max (m/s²)",
  "external.sprint_max": "Speed max (km/h)",
  "external.global_distance": "Total Distance (km)",
};

// --------- Build KPI / ByPosition / Trainability ---------
function buildSheets({ teamStats, byPosition, topRii, lowTrain }) {
  const kpiRows = [
    ["Metric", "Value", "Delta vs prev."],
    ["Intensity (PT)", fmtVal(teamStats?.avgIntensityPT), fmtDelta(teamStats?.deltas?.avgIntensityPT)],
    ["Mechanical (PT)", fmtVal(teamStats?.avgMechPT), fmtDelta(teamStats?.deltas?.avgMechPT)],
    ["RII", fmtVal(teamStats?.avgRII, { percent: true }), fmtDelta(teamStats?.deltas?.avgRII, { percent: true })],
    ["MLI", fmtVal(teamStats?.avgMLI, { percent: true }), fmtDelta(teamStats?.deltas?.avgMLI, { percent: true })],
    ["Fatigue", fmtVal(teamStats?.avgFatigue, { percent: true }), fmtDelta(teamStats?.deltas?.avgFatigue, { percent: true })],
    ["Acc max (m/s²)", fmtVal(teamStats?.avgAcc), fmtDelta(teamStats?.deltas?.avgAcc)],
    ["Dec max (m/s²)", fmtVal(teamStats?.avgDec), fmtDelta(teamStats?.deltas?.avgDec)],
    ["Sprint max (km/h)", fmtVal(teamStats?.avgSprint), fmtDelta(teamStats?.deltas?.avgSprint)],
    ["Global distance (km)", fmtVal(teamStats?.sumDistanceKm), fmtDelta(teamStats?.deltas?.sumDistanceKm)],
    ["Total COD", fmtVal(teamStats?.sumCod, { decimals: 0 }), fmtDelta(teamStats?.deltas?.sumCod, { decimals: 0 })],
  ];

  const byPosHeader = [
    "Position", "Count",
    "Intensity", "Δ Intensity",
    "Mechanical", "Δ Mechanical",
    "RII", "Δ RII",
    "MLI", "Δ MLI",
    "Fatigue", "Δ Fatigue",
    "Acc (m/s²)", "Δ Acc",
    "Dec (m/s²)", "Δ Dec",
    "Sprint (km/h)", "Δ Sprint",
  ];
  const byPosRows = [byPosHeader];
  (byPosition || []).forEach((r) => {
    byPosRows.push([
      r.pos, r.n ?? "",
      fmtVal(r.intensityPT), fmtDelta(r.d_intensityPT),
      fmtVal(r.mechanicalPT), fmtDelta(r.d_mechanicalPT),
      fmtVal(r.rii, { percent:true }), fmtDelta(r.d_rii, { percent:true }),
      fmtVal(r.mli, { percent:true }), fmtDelta(r.d_mli, { percent:true }),
      fmtVal(r.fatigue, { percent:true }), fmtDelta(r.d_fatigue, { percent:true }),
      fmtVal(r.acc), fmtDelta(r.d_acc),
      fmtVal(r.dec), fmtDelta(r.d_dec),
      fmtVal(r.sprint), fmtDelta(r.d_sprint),
    ]);
  });

  const bestScoreRows = [["Rank", "Player", "Position", "Score (/10)", "Delta", "Avatar?"]];
  (topRii || []).forEach((p, i) => {
    bestScoreRows.push([
      i + 1,
      p.name || "",
      p.position || "",
      fmtTrainabilityScore(p),
      fmtDelta(p.delta, { unit: " pts", decimals: 1 }),
      p.avatar ? "yes" : "no",
    ]);
  });

  const lowestScoreRows = [["Rank", "Player", "Position", "Score (/10)", "Delta", "Avatar?"]];
  (lowTrain || []).forEach((p, i) => {
    lowestScoreRows.push([
      i + 1,
      p.name || "",
      p.position || "",
      fmtTrainabilityScore(p),
      fmtDelta(p.delta, { unit: " pts", decimals: 1 }),
      p.avatar ? "yes" : "no",
    ]);
  });

  return {
    KPI_Team: kpiRows,
    ByPosition: byPosRows,
    TrainabilityBest: bestScoreRows,
    TrainabilityLowest: lowestScoreRows,
  };
}

// --------- Build Trends (time-series) sheets from PdfExportContext ---------
function buildTrendsFromExporter(exporterData) {
  const series = exporterData?.__series || {};                 // { metricKey: [ {date, player1, ..., __median, __band_lo, __band_hi}, ... ] }
  const internalKeys = exporterData?.__club_internal_keys || [];
  const externalKeys = exporterData?.__club_external_keys || [];

  const buildSheet = (metricKey, rows) => {
    // collect all player names present in any row
    const players = new Set();
    rows.forEach(r => {
      Object.keys(r || {}).forEach(k => {
        if (!k || k === "date" || k.startsWith("__")) return;
        players.add(k);
      });
    });
    const playerCols = [...players].sort((a, b) => a.localeCompare(b));

    const header = ["Date", "Median", "Band lo", "Band hi", ...playerCols];
    const body = rows.map(r => {
      const line = [
        r?.date ?? "",
        (Number.isFinite(r?.__median) ? r.__median : ""),
        (Number.isFinite(r?.__band_lo) ? r.__band_lo : ""),
        (Number.isFinite(r?.__band_hi) ? r.__band_hi : ""),
      ];
      playerCols.forEach(name => {
        const v = r?.[name];
        line.push(Number.isFinite(v) ? v : "");
      });
      return line;
    });

    return [header, ...body];
  };

  const toLabel = (metricKey) => {
    // metricKey is like "intensity_index" or "sprint_max".
    // We’ll prefix with internal/external for sheet name display via caller.
    return METRIC_LABELS[`internal.${metricKey}`] ||
           METRIC_LABELS[`external.${metricKey}`] ||
           metricKey;
  };

  // Return a flat list of { group: "Internal"|"External", metricKey, label, rows }
  const out = [];

  internalKeys.forEach((fullKey) => {
    // fullKey format from context: "club.internal.<metricKey>"
    const metricKey = fullKey.split(".").slice(2).join("."); // keep after 'club.internal'
    const rows = series[metricKey] || [];
    out.push({ group: "Internal", metricKey, label: toLabel(metricKey), rows: buildSheet(metricKey, rows) });
  });
  externalKeys.forEach((fullKey) => {
    const metricKey = fullKey.split(".").slice(2).join(".");
    const rows = series[metricKey] || [];
    out.push({ group: "External", metricKey, label: toLabel(metricKey), rows: buildSheet(metricKey, rows) });
  });

  return out;
}

// CSV unique “aplati”, now with Trends
function buildSingleCsvRows({ teamStats, byPosition, topRii, lowTrain, trends }) {
  const rows = [["dataset","group","metric_key","metric_label","date","position","player","value","delta","extra","median","band_lo","band_hi"]];
  const push = (...r) => rows.push(r.map(x => x ?? ""));

  // KPI team
  const k = teamStats || {};
  push("KPI_Team","","","Intensity (PT)","", "", "", fmtVal(k.avgIntensityPT), fmtDelta(k.deltas?.avgIntensityPT), "", "", "", "");
  push("KPI_Team","","","Mechanical (PT)","", "", "", fmtVal(k.avgMechPT), fmtDelta(k.deltas?.avgMechPT), "", "", "", "");
  push("KPI_Team","","","RII","", "", "", fmtVal(k.avgRII, { percent:true }), fmtDelta(k.deltas?.avgRII, { percent:true }), "", "", "", "");
  push("KPI_Team","","","MLI","", "", "", fmtVal(k.avgMLI, { percent:true }), fmtDelta(k.deltas?.avgMLI, { percent:true }), "", "", "", "");
  push("KPI_Team","","","Fatigue","", "", "", fmtVal(k.avgFatigue, { percent:true }), fmtDelta(k.deltas?.avgFatigue, { percent:true }), "", "", "", "");
  push("KPI_Team","","","Acc max (m/s²)","", "", "", fmtVal(k.avgAcc), fmtDelta(k.deltas?.avgAcc), "", "", "", "");
  push("KPI_Team","","","Dec max (m/s²)","", "", "", fmtVal(k.avgDec), fmtDelta(k.deltas?.avgDec), "", "", "", "");
  push("KPI_Team","","","Sprint max (km/h)","", "", "", fmtVal(k.avgSprint), fmtDelta(k.deltas?.avgSprint), "", "", "", "");
  push("KPI_Team","","","Global distance (km)","", "", "", fmtVal(k.sumDistanceKm), fmtDelta(k.deltas?.sumDistanceKm), "", "", "", "");
  push("KPI_Team","","","Total COD","", "", "", fmtVal(k.sumCod, { decimals:0 }), fmtDelta(k.deltas?.sumCod, { decimals:0 }), "", "", "", "");

  // ByPosition
  (byPosition || []).forEach(r => {
    push("ByPosition","","Intensity (PT)","", "", r.pos, "", fmtVal(r.intensityPT), fmtDelta(r.d_intensityPT), `n=${r.n ?? ""}`, "", "", "");
    push("ByPosition","","Mechanical (PT)","", "", r.pos, "", fmtVal(r.mechanicalPT), fmtDelta(r.d_mechanicalPT), `n=${r.n ?? ""}`, "", "", "");
    push("ByPosition","","RII","", "", r.pos, "", fmtVal(r.rii, { percent:true }), fmtDelta(r.d_rii, { percent:true }), `n=${r.n ?? ""}`, "", "", "");
    push("ByPosition","","MLI","", "", r.pos, "", fmtVal(r.mli, { percent:true }), fmtDelta(r.d_mli, { percent:true }), `n=${r.n ?? ""}`, "", "", "");
    push("ByPosition","","Fatigue","", "", r.pos, "", fmtVal(r.fatigue, { percent:true }), fmtDelta(r.d_fatigue, { percent:true }), `n=${r.n ?? ""}`, "", "", "");
    push("ByPosition","","Acc (m/s²)","", "", r.pos, "", fmtVal(r.acc), fmtDelta(r.d_acc), `n=${r.n ?? ""}`, "", "", "");
    push("ByPosition","","Dec (m/s²)","", "", r.pos, "", fmtVal(r.dec), fmtDelta(r.d_dec), `n=${r.n ?? ""}`, "", "", "");
    push("ByPosition","","Sprint (km/h)","", "", r.pos, "", fmtVal(r.sprint), fmtDelta(r.d_sprint), `n=${r.n ?? ""}`, "", "", "");
  });

  // Trainability score leaders
  (topRii || []).forEach((p, i) => {
    push("TrainabilityBest","","trainability_score","Score (/10)", "", p.position || "", p.name || "", fmtTrainabilityScore(p), fmtDelta(p.delta, { unit: " pts", decimals: 1 }), `rank=${i+1}`, "", "", "");
  });
  (lowTrain || []).forEach((p, i) => {
    push("TrainabilityLowest","","trainability_score","Score (/10)", "", p.position || "", p.name || "", fmtTrainabilityScore(p), fmtDelta(p.delta, { unit: " pts", decimals: 1 }), `rank=${i+1}`, "", "", "");
  });

  // Trends (flatten): one row per (date, player) + metric metadata & median/bands
  (trends || []).forEach(({ group, metricKey, label, rows: sheet }) => {
    if (!sheet || sheet.length === 0) return;
    const header = sheet[0];                 // ["Date","Median","Band lo","Band hi", ...players]
    const playerCols = header.slice(4);
    for (let i = 1; i < sheet.length; i++) {
      const line = sheet[i];
      const date = line[0];
      const median = line[1];
      const lo = line[2];
      const hi = line[3];
      playerCols.forEach((playerName, idx) => {
        const v = line[4 + idx];
        const val = (v === "" || v == null) ? "" : v;
        // delta (prev) is not defined in trends table; keep empty for consistency
        rows.push([
          "Trends",
          group,
          metricKey,
          label,
          date,
          "",              // position (n/a at this level)
          playerName,
          val,
          "",              // delta
          "",              // extra
          median,
          lo,
          hi
        ]);
      });
    }
  });

  return rows;
}

export default function ExportClubReportMenu({
  season,
  teamStats,
  byPosition,
  topRii = [],
  lowTrain = [],
  className = "",
  dark = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const exporter = usePdfExport(); // << NEW: read trends from context

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, []);

  const baseSheets = useMemo(
    () => buildSheets({ teamStats, byPosition, topRii, lowTrain }),
    [teamStats, byPosition, topRii, lowTrain]
  );

  // Build trends once from exporter
  const trends = useMemo(() => {
    const data = exporter?.getAllData ? exporter.getAllData() : {};
    return buildTrendsFromExporter(data);
  }, [exporter]);

  const handleExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(baseSheets.KPI_Team), "KPI_Team");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(baseSheets.ByPosition), "ByPosition");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(baseSheets.TrainabilityBest), "TrainabilityBest");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(baseSheets.TrainabilityLowest), "TrainabilityLowest");

    // Trends: one sheet per metric
    trends.forEach(({ group, metricKey, label, rows }) => {
      const sheetName = `${group}.${metricKey}`.slice(0, 31); // Excel limit
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), sheetName);
    });

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `club_report_${season}.xlsx`);
    setOpen(false);
  };

  const handleCsvZip = async () => {
    const zip = new JSZip();
    zip.file("KPI_Team.csv", toCSV(baseSheets.KPI_Team));
    zip.file("ByPosition.csv", toCSV(baseSheets.ByPosition));
    zip.file("TrainabilityBest.csv", toCSV(baseSheets.TrainabilityBest));
    zip.file("TrainabilityLowest.csv", toCSV(baseSheets.TrainabilityLowest));

    // Trends: folder + files
    trends.forEach(({ group, metricKey, rows }) => {
      const folder = `Trends/${group}`;
      const name = `${folder}/${metricKey}.csv`;
      zip.file(name, toCSV(rows));
    });

    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, `club_report_${season}.zip`);
    setOpen(false);
  };

  const handleCsvSingle = async () => {
    const rows = buildSingleCsvRows({
      teamStats,
      byPosition,
      topRii,
      lowTrain,
      trends,
    });
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `club_report_${season}_all.csv`);
    setOpen(false);
  };

  const PdfItem = () => (
    <div onClick={() => setOpen(false)}>
      <DownloadClubPdfButton
        club={{ name: "" }}
        season={season}
        teamStats={teamStats}
        byPosition={byPosition}
        asMenuItem
      />
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className={[
          "inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border shadow-sm",
          dark
            ? "bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700"
            : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50",
        ].join(" ")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Export
        <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2"/>
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className={[
            "absolute z-50 mt-2 w-56 rounded-md border shadow-lg p-2",
            dark ? "border-gray-700 bg-gray-900 text-gray-100 shadow-black/30" : "border-gray-200 bg-white text-gray-800",
          ].join(" ")}
        >
          {/* PDF item, garde la fermeture auto */}
          <div onClick={() => setOpen(false)}>
            <DownloadClubPdfButton
              club={{ name: "" }}
              season={season}
              teamStats={teamStats}
              byPosition={byPosition}
              asMenuItem
              className={["w-full text-left px-3 py-2 text-sm rounded-md", dark ? "hover:bg-gray-800" : "hover:bg-gray-50"].join(" ")}
            />
          </div>
          <div className={["h-px my-2", dark ? "bg-gray-700" : "bg-gray-200"].join(" ")} />
          <button role="menuitem" onClick={handleExcel} className={["w-full text-left px-3 py-2 text-sm rounded-md", dark ? "hover:bg-gray-800" : "hover:bg-gray-50"].join(" ")}>
            Excel (.xlsx)
          </button>
          <button role="menuitem" onClick={handleCsvZip} className={["w-full text-left px-3 py-2 text-sm rounded-md", dark ? "hover:bg-gray-800" : "hover:bg-gray-50"].join(" ")}>
            CSV – plusieurs fichiers (.zip)
          </button>
          <button role="menuitem" onClick={handleCsvSingle} className={["w-full text-left px-3 py-2 text-sm rounded-md", dark ? "hover:bg-gray-800" : "hover:bg-gray-50"].join(" ")}>
            CSV – fichier unique
          </button>
        </div>
      )}
    </div>
  );
}
